import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import PackageManagement from './PackageManagement';
import AdminRequests from './AdminRequests';
import AdminManagement from './AdminManagement';
import BranchPricingManager from './BranchPricingManager';
import CustomPackageRequests from './CustomPackageRequests';
import ContactManagement from './ContactManagement';
import DestinationSpotManager from './DestinationSpotManager';
import BranchManager from './BranchManager';
import AboutFeaturesManager from './AboutFeaturesManager';
import AboutPageManager from './AboutPageManager';
import GuideManager from './GuideManager';
import DestinationGalleryManager from './DestinationGalleryManager';
import SocialLinksManager from './SocialLinksManager'; // ✅ NEW IMPORT
import ContactMessagesManager from './ContactMessagesManager';
import BookingManager from './BookingManager';

const AdminDashboard = ({ currentUser }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [message, setMessage] = useState('');

    const { logout } = useAdmin();

    const renderTabContent = () => {
        switch (activeTab) {
            case 'dashboard':
                return <DashboardOverview setMessage={setMessage} />;
            case 'packages':
                return <PackageManagement setMessage={setMessage} />;
            case 'spots':
                return <DestinationSpotManager setMessage={setMessage} />;
            case 'branches':
                return <BranchManager setMessage={setMessage} />;
            case 'custom-packages':
                return <CustomPackageRequests setMessage={setMessage} />;
            case 'branch-pricing':
                return <BranchPricingManager setMessage={setMessage} />;
            case 'contact':
                return <ContactManagement setMessage={setMessage} />;
            case 'requests':
                return <AdminRequests setMessage={setMessage} />;
            case 'admins':
                return <AdminManagement setMessage={setMessage} />;
            case 'about-features':
                return <AboutFeaturesManager setMessage={setMessage} />;
            case 'about-page':
                return <AboutPageManager setMessage={setMessage} />;
            case 'guide':
                return <GuideManager setMessage={setMessage} />;
            case 'destination-gallery':
                return <DestinationGalleryManager setMessage={setMessage} />;
            case 'social-links': // ✅ NEW CASE
                return <SocialLinksManager setMessage={setMessage} />;
            case 'contact-messages':
                return <ContactMessagesManager setMessage={setMessage} />;
            case 'bookings':
                return <BookingManager setMessage={setMessage} />;
            default:
                return <DashboardOverview setMessage={setMessage} />;
        }
    };
    

    // DashboardOverview Component
    const DashboardOverview = ({ setMessage }) => {
        const { apiRequest } = useAdmin();
        const [stats, setStats] = useState(null);
        const [loading, setLoading] = useState(true);
        const [recentRequests, setRecentRequests] = useState([]);
        const [recentRequestsLoading, setRecentRequestsLoading] = useState(true);

        useEffect(() => {
            loadDashboardStats();
            loadRecentRequests();
        }, []);

        const loadDashboardStats = async () => {
            try {
                setLoading(true);

                const promises = [
                    apiRequest('/packages/stats').catch(error => {
                        console.warn('Package stats error:', error.message);
                        return { stats: { total: 0, active: 0, inactive: 0 } };
                    }),
                    apiRequest('/custom-packages/requests-stats').catch(error => {
                        console.warn('Custom package stats error:', error.message);
                        return { stats: { total: 0, pending: 0, confirmed: 0, rejected: 0, revenue: { totalRevenue: 0 } } };
                    }),
                    apiRequest('/destination-spots/admin/stats').catch(error => {
                        console.warn('Destination spots stats error:', error.message);
                        return { stats: { total: 0, active: 0, inactive: 0 } };
                    }),
                    apiRequest('/about-features/admin/stats').catch(error => {
                        console.warn('About features stats error:', error.message);
                        return { stats: { total: 0, active: 0, inactive: 0 } };
                    }),
                    apiRequest('/social-links/stats').catch(error => {
                        console.warn('Social links stats error:', error.message);
                        return { stats: { total: 0, active: 0, inactive: 0 } };
                    }),
                    apiRequest('/bookings/stats/overview').catch(error => { // ✅ NEW
                        console.warn('Booking stats error:', error.message);
                        return { stats: { total: 0, pending: 0, confirmed: 0, cancelled: 0, completed: 0 } };
                    })
                ];

                const [packageStats, customStats, spotStats, featureStats, socialLinksStats, bookingStats] = await Promise.all(promises);

                setStats({
                    packages: packageStats.stats,
                    customPackages: customStats.stats,
                    spots: spotStats.stats,
                    features: featureStats.stats,
                    socialLinks: socialLinksStats.stats,
                    bookings: bookingStats.stats // ✅ NEW
                });

            } catch (error) {
                console.error('Dashboard stats loading error:', error);
                setMessage({ type: 'error', text: 'Error loading dashboard stats' });

                setStats({
                    packages: { total: 0, active: 0, inactive: 0 },
                    customPackages: { total: 0, pending: 0, confirmed: 0, rejected: 0, revenue: { totalRevenue: 0 } },
                    spots: { total: 0, active: 0, inactive: 0 },
                    features: { total: 0, active: 0, inactive: 0 },
                    socialLinks: { total: 0, active: 0, inactive: 0 },
                    bookings: { total: 0, pending: 0, confirmed: 0, cancelled: 0, completed: 0 } // ✅ NEW
                });
            } finally {
                setLoading(false);
            }
        };

        const loadRecentRequests = async () => {
            try {
                setRecentRequestsLoading(true);
                const data = await apiRequest('/custom-packages/recent-requests');
                if (data.success) {
                    setRecentRequests(data.requests || []);
                }
            } catch (error) {
                console.error('Error loading recent requests:', error);
                setRecentRequests([]);
            } finally {
                setRecentRequestsLoading(false);
            }
        };

        // AdminDashboard.js - loadDashboardStats function এ যোগ করুন:
        

        if (loading) {
            return (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Loading dashboard...</p>
                </div>
            );
        }

        return (
            <div className="container-fluid">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow-sm">
                            <div className="card-header bg-primary text-white">
                                <h4 className="mb-0">
                                    <i className="fas fa-tachometer-alt me-2"></i>
                                    Dashboard Overview
                                </h4>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    {/* Packages Stats */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.packages?.total || 0}</h4>
                                        <small>Total Packages</small>
                                    </div>
                                    <i className="fas fa-box fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Custom Packages Stats */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.customPackages?.total || 0}</h4>
                                        <small>Custom Requests</small>
                                    </div>
                                    <i className="fas fa-clipboard-list fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Destination Spots Stats */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.spots?.total || 0}</h4>
                                        <small>Destination Spots</small>
                                    </div>
                                    <i className="fas fa-map-marker-alt fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Social Links Stats - ✅ NEW */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-secondary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.socialLinks?.total || 0}</h4>
                                        <small>Social Links</small>
                                    </div>
                                    <i className="fas fa-share-alt fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Second Row Stats */}
                <div className="row mb-4">
                    {/* Revenue Stats */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-dark">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">৳{(stats?.customPackages?.revenue?.totalRevenue || 0).toLocaleString()}</h4>
                                        <small>Total Revenue</small>
                                    </div>
                                    <i className="fas fa-money-bill-wave fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pending Requests */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.customPackages?.pending || 0}</h4>
                                        <small>Pending Requests</small>
                                    </div>
                                    <i className="fas fa-clock fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Confirmed Requests */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.customPackages?.confirmed || 0}</h4>
                                        <small>Confirmed</small>
                                    </div>
                                    <i className="fas fa-check-circle fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Active Social Links - ✅ NEW */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.socialLinks?.active || 0}</h4>
                                        <small>Active Social Links</small>
                                    </div>
                                    <i className="fas fa-check fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Booking Stats */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-secondary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.bookings?.total || 0}</h4>
                                        <small>Total Bookings</small>
                                    </div>
                                    <i className="fas fa-calendar-check fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Pending Bookings */}
                    <div className="col-md-3 mb-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between">
                                    <div>
                                        <h4 className="mb-0">{stats?.bookings?.pending || 0}</h4>
                                        <small>Pending Bookings</small>
                                    </div>
                                    <i className="fas fa-clock fa-2x opacity-50"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-bolt me-2"></i>
                                    Quick Actions
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-primary w-100 h-100 py-3"
                                            onClick={() => setActiveTab('spots')}
                                        >
                                            <i className="fas fa-map-marker-alt fa-2x mb-2"></i>
                                            <br />
                                            <small>Manage Spots</small>
                                        </button>
                                    </div>
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-success w-100 h-100 py-3"
                                            onClick={() => setActiveTab('custom-packages')}
                                        >
                                            <i className="fas fa-clipboard-list fa-2x mb-2"></i>
                                            <br />
                                            <small>View Requests</small>
                                        </button>
                                    </div>
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-info w-100 h-100 py-3"
                                            onClick={() => setActiveTab('branch-pricing')}
                                        >
                                            <i className="fas fa-money-bill-wave fa-2x mb-2"></i>
                                            <br />
                                            <small>Pricing Setup</small>
                                        </button>
                                    </div>
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-warning w-100 h-100 py-3"
                                            onClick={() => setActiveTab('branches')}
                                        >
                                            <i className="fas fa-building fa-2x mb-2"></i>
                                            <br />
                                            <small>Manage Branches</small>
                                        </button>
                                    </div>
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-secondary w-100 h-100 py-3"
                                            onClick={() => setActiveTab('social-links')} // ✅ NEW
                                        >
                                            <i className="fas fa-share-alt fa-2x mb-2"></i>
                                            <br />
                                            <small>Social Links</small>
                                        </button>
                                    </div>
                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-dark w-100 h-100 py-3"
                                            onClick={() => setActiveTab('about-page')}
                                        >
                                            <i className="fas fa-info-circle fa-2x mb-2"></i>
                                            <br />
                                            <small>About Page</small>
                                        </button>
                                    </div>

                                    <div className="col-md-2 col-sm-4">
                                        <button
                                            className="btn btn-outline-success w-100 h-100 py-3"
                                            onClick={() => setActiveTab('bookings')}
                                        >
                                            <i className="fas fa-calendar-check fa-2x mb-2"></i>
                                            <br />
                                            <small>Manage Bookings</small>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Requests */}
                <div className="row">
                    <div className="col-lg-8">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-clock me-2"></i>
                                    Recent Custom Package Requests
                                </h5>
                            </div>
                            <div className="card-body">
                                {recentRequestsLoading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="mt-2 text-muted small">Loading recent requests...</p>
                                    </div>
                                ) : recentRequests.length === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="fas fa-inbox fa-2x text-muted mb-3"></i>
                                        <p className="text-muted">No recent requests found</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Name</th>
                                                    <th>Date</th>
                                                    <th>Status</th>
                                                    <th>Price</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentRequests.map((request) => (
                                                    <tr key={request._id}>
                                                        <td>
                                                            <small className="text-muted">#{request._id.substring(0, 8)}</small>
                                                        </td>
                                                        <td>{request.fullName}</td>
                                                        <td>
                                                            <small>
                                                                {new Date(request.createdAt).toLocaleDateString()}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${request.status === 'pending' ? 'bg-warning' :
                                                                request.status === 'confirmed' ? 'bg-success' :
                                                                    request.status === 'rejected' ? 'bg-danger' : 'bg-secondary'
                                                                }`}>
                                                                {request.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <strong className="text-success">
                                                                ৳{request.estimatedPrice?.toLocaleString()}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => setActiveTab('custom-packages')}
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* System Status */}
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-server me-2"></i>
                                    System Status
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="list-group list-group-flush">
                                    <div className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Database</span>
                                        <span className="badge bg-success">Online</span>
                                    </div>
                                    <div className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>API Server</span>
                                        <span className="badge bg-success">Online</span>
                                    </div>
                                    <div className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Storage</span>
                                        <span className="badge bg-success">92% Free</span>
                                    </div>
                                    <div className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Last Backup</span>
                                        <span className="text-muted small">Today, 02:00 AM</span>
                                    </div>
                                    <div className="list-group-item d-flex justify-content-between align-items-center">
                                        <span>Uptime</span>
                                        <span className="text-muted small">99.8%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="card mt-4">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    <i className="fas fa-chart-bar me-2"></i>
                                    Quick Stats
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="row text-center">
                                    <div className="col-6 mb-3">
                                        <div className="bg-light rounded p-3">
                                            <h3 className="text-primary mb-0">{stats?.packages?.active || 0}</h3>
                                            <small className="text-muted">Active Packages</small>
                                        </div>
                                    </div>
                                    <div className="col-6 mb-3">
                                        <div className="bg-light rounded p-3">
                                            <h3 className="text-success mb-0">{stats?.features?.active || 0}</h3>
                                            <small className="text-muted">Active Features</small>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="bg-light rounded p-3">
                                            <h3 className="text-info mb-0">{stats?.socialLinks?.inactive || 0}</h3> {/* ✅ NEW */}
                                            <small className="text-muted">Inactive Social Links</small>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="bg-light rounded p-3">
                                            <h3 className="text-warning mb-0">{stats?.customPackages?.rejected || 0}</h3>
                                            <small className="text-muted">Rejected Requests</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-vh-100 bg-light">
            {/* Header */}
            <nav className="navbar navbar-expand-lg navbar-dark bg-primary shadow-sm">
                <div className="container-fluid">
                    <span className="navbar-brand mb-0 h1 d-flex align-items-center">
                        <i className="fas fa-cog me-2"></i>
                        Admin Panel
                    </span>

                    <div className="d-flex align-items-center">
                        <div className="text-white me-3 text-end">
                            <div className="small">Welcome,</div>
                            <div className="fw-bold">{currentUser?.name}</div>
                        </div>
                        <button
                            onClick={logout}
                            className="btn btn-outline-light btn-sm"
                        >
                            <i className="fas fa-sign-out-alt me-1"></i>
                            Sign Out
                        </button>
                    </div>
                </div>
            </nav>

            <div className="container-fluid py-4">
                {/* Message Alert */}
                {message && (
                    <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'} alert-dismissible fade show mb-4`}>
                        <i className={`fas ${message.type === 'error' ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2`}></i>
                        {message.text}
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage('')}
                        ></button>
                    </div>
                )}

                <div className="card shadow-sm">
                    <div className="card-header bg-white border-bottom-0 pb-0">
                        <ul className="nav nav-tabs card-header-tabs">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('dashboard')}
                                >
                                    <i className="fas fa-tachometer-alt me-2"></i>
                                    Dashboard
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'social-links' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('social-links')} // ✅ NEW TAB
                                >
                                    <i className="fas fa-share-alt me-2"></i>
                                    Social Links
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'spots' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('spots')}
                                >
                                    <i className="fas fa-map-marker-alt me-2"></i>
                                    Destination Spots
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'branches' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('branches')}
                                >
                                    <i className="fas fa-building me-2"></i>
                                    Branches
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'about-features' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('about-features')}
                                >
                                    <i className="fas fa-list-check me-2"></i>
                                    About Features
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'about-page' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('about-page')}
                                >
                                    <i className="fas fa-info-circle me-2"></i>
                                    About Page
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'custom-packages' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('custom-packages')}
                                >
                                    <i className="fas fa-clipboard-list me-2"></i>
                                    Custom Requests
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'packages' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('packages')}
                                >
                                    <i className="fas fa-box me-2"></i>
                                    Packages
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'branch-pricing' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('branch-pricing')}
                                >
                                    <i className="fas fa-money-bill-wave me-2"></i>
                                    Branch Pricing
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('contact')}
                                >
                                    <i className="fas fa-address-book me-2"></i>
                                    Contact Info
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'requests' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('requests')}
                                >
                                    <i className="fas fa-users me-2"></i>
                                    Admin Requests
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'admins' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('admins')}
                                >
                                    <i className="fas fa-user-shield me-2"></i>
                                    Admin Management
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'guide' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('guide')}
                                >
                                    <i className="fas fa-user-tie me-2"></i>
                                    Manage Team Member
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'destination-gallery' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('destination-gallery')}
                                >
                                    <i className="fas fa-images me-2"></i>
                                    Destination Gallery
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'contact-messages' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('contact-messages')}
                                >
                                    <i className="fas fa-envelope me-2"></i>
                                    Contact Messages
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'bookings' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('bookings')}
                                >
                                    <i className="fas fa-calendar-check me-2"></i>
                                    Bookings
                                </button>
                            </li>
                        </ul>
                    </div>

                    <div className="card-body p-4">
                        {renderTabContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;